import { isStandalone } from './pwa.js';

// The installed app on iOS (owner report 30/10/2026: the tab bar floating in the middle of the page, a keyboard's height
// too high, until the app was closed): after the keyboard closes, iOS can leave the web view's viewport short, and
// everything position: fixed sits against that short bottom. It's WebKit's bug (bugs.webkit.org 297779, Apple forums
// thread 800125), unfixed, and only a restart of the app clears it by itself. A sync display flip of the full-height root
// makes WebKit measure the viewport again. Only in the installed app, where the height changes for no other reason than
// the keyboard or a rotation, and only when the numbers say it's stuck: a healthy phone never flips.

/** After the field lets go: the keyboard takes a moment to leave, and a late resize can follow. */
export const HEAL_DELAYS_MS = [140, 700];
/** Short by more than this is stuck (px): rounding moves the numbers by less. */
export const VIEWPORT_STUCK_PX = 4;

const NOT_TEXT = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

const isTextField = (el) => {
  if (!el?.tagName) return false;
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
  return el.tagName === 'INPUT' && !NOT_TEXT.has(el.type);
};

/** How many px short of its full height the viewport is; 0 when fine. A pinch zoom shrinks the visual viewport on purpose. */
export const viewportShort = (full, s) => {
  if (s.scale > 1.01) return 0;
  const short = Math.max(full - s.innerHeight, s.innerHeight - s.visualHeight, s.offsetTop);
  return short > VIEWPORT_STUCK_PX ? Math.round(short) : 0;
};

export const viewportStuckMessage = ({ short, after, sample }) =>
  `viewport stuck: short ${short}px, after repair ${after}px (inner ${Math.round(sample.innerHeight)}, visual ${Math.round(sample.visualHeight)}, top ${Math.round(sample.offsetTop)})`;

const reflowRoot = (win) => {
  const root = win.document.getElementById('root');
  if (!root) return;
  const y = win.scrollY;
  // The page collapses for that instant: the window's scroll and the rows scrolled inside it go back before anything paints.
  const rows = Array.from(root.querySelectorAll('*'))
    .filter((el) => el.scrollTop > 0 || el.scrollLeft > 0)
    .map((el) => ({ el, top: el.scrollTop, left: el.scrollLeft }));
  const { display } = root.style;
  root.style.display = 'none';
  void root.offsetHeight;
  root.style.display = display;
  if (y > 0) win.scrollTo({ top: y, behavior: 'instant' });
  rows.forEach(({ el, top, left }) => {
    el.scrollTop = top;
    el.scrollLeft = left;
  });
};

/** Installs the repair once (main.jsx); returns its undo. `onStuck` says how it went: the only evidence from real phones. */
export const installViewportHeal = (onStuck = () => undefined, win = window) => {
  const vv = win.visualViewport;
  if (!vv || !isStandalone(win)) return () => undefined;
  const doc = win.document;
  let full = win.innerHeight;
  let width = win.innerWidth;
  // One repair per keyboard: one that didn't help must never flicker in a loop.
  let tried = false;
  let timers = [];

  const sample = () => ({ innerHeight: win.innerHeight, visualHeight: vv.height, offsetTop: vv.offsetTop, scale: vv.scale });
  const clear = () => {
    timers.forEach((t) => win.clearTimeout(t));
    timers = [];
  };
  const track = () => {
    if (win.innerWidth !== width) {
      width = win.innerWidth;
      full = win.innerHeight;
    } else if (win.innerHeight > full) full = win.innerHeight;
  };
  const repair = () => {
    if (tried || isTextField(doc.activeElement)) return;
    const before = sample();
    const short = viewportShort(full, before);
    if (!short) return;
    tried = true;
    reflowRoot(win);
    onStuck({ short, after: viewportShort(full, sample()), sample: before });
  };
  const schedule = () => {
    clear();
    timers = HEAL_DELAYS_MS.map((ms) => win.setTimeout(repair, ms));
  };
  // A field taking focus is a new keyboard; a dialog or a button taking it must not cancel the repair.
  const onFocusIn = (e) => {
    if (!isTextField(e.target)) return;
    clear();
    tried = false;
  };
  const onVisible = () => {
    if (doc.visibilityState !== 'visible') return;
    tried = false;
    schedule();
  };

  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('focusout', schedule);
  doc.addEventListener('visibilitychange', onVisible);
  win.addEventListener('resize', track);
  vv.addEventListener('resize', track);
  return () => {
    clear();
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('focusout', schedule);
    doc.removeEventListener('visibilitychange', onVisible);
    win.removeEventListener('resize', track);
    vv.removeEventListener('resize', track);
  };
};
